// src/pages/LendingBookings.js

import React, { useState, useEffect } from 'react';
import { Container, Typography, List, Divider, Box, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { getUserTransactions } from '../services/api';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useAuth } from '../context/AuthContext';
import BookingCategoryItem from '../components/BookingCategoryItem';

function LendingBookings() {
    const { currentUser, loading } = useAuth();
    const [bookingCounts, setBookingCounts] = useState({
        active: 0,
        completed: 0,
        outdated: 0
    });
    const navigate = useNavigate();

    // Move fetch logic inline to avoid listing it as a dependency
    useEffect(() => {
        if (!loading && currentUser) {
            const fetchBookings = async () => {
                try {
                    const response = await getUserTransactions();
                    // Filter bookings where current user is the lender
                    const lenderBookings = response.data.filter(
                        (booking) => booking.lender_id === currentUser.id
                    );
                    const counts = {
                        active: lenderBookings.filter((booking) =>
                            ['pending', 'accepted', 'paid', 'in_progress', 'booked', 'picked up'].includes(
                                booking.status.toLowerCase()
                            )
                        ).length,
                        completed: lenderBookings.filter((booking) =>
                            ['returned', 'rated'].includes(booking.status.toLowerCase())
                        ).length,
                        outdated: lenderBookings.filter((booking) =>
                            ['cancelled', 'expired'].includes(booking.status.toLowerCase())
                        ).length
                    };
                    setBookingCounts(counts);
                } catch (error) {
                    console.error('Error fetching lending bookings:', error);
                }
            };

            fetchBookings();
        }
    }, [loading, currentUser]);

    const bookingCategories = [
        {
            title: 'Active Requests',
            count: bookingCounts.active,
            description: 'Pending requests and ongoing bookings for your items',
            icon: <AssignmentIcon />,
            to: '/my-lending-bookings'
        },
        {
            title: 'Completed Bookings',
            count: bookingCounts.completed,
            description: "All completed bookings for your items. Don't forget to leave a review!",
            icon: <CheckCircleIcon />,
            to: '/lending-completed-bookings'
        },
        {
            title: 'Cancelled Bookings',
            count: bookingCounts.outdated,
            description: 'Cancelled or expired booking requests for your items',
            icon: <CancelIcon />,
            to: '/lending-outdated-bookings'
        }
    ];

    if (loading) {
        return (
            <Container maxWidth="md" sx={{ mt: 4 }}>
                <Typography variant="h4" gutterBottom>
                    Lending Bookings
                </Typography>
                <Typography variant="body1" paragraph>
                    Loading your lending bookings...
                </Typography>
            </Container>
        );
    }

    if (!currentUser) {
        return (
            <Container maxWidth="md" sx={{ mt: 4 }}>
                <Typography variant="h4" gutterBottom>
                    Lending Bookings
                </Typography>
                <Typography variant="body1" paragraph>
                    You need to be logged in to view your lending bookings.
                </Typography>
            </Container>
        );
    }

    return (
        <Container maxWidth="md" sx={{ mt: 4 }}>
            <Box display="flex" alignItems="center" mb={2}>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ mr: 2 }}>
                    Back
                </Button>
                <Typography variant="h4" component="h1" flexGrow={1}>
                    What I'm lending...
                </Typography>
            </Box>
            <Typography variant="body1" paragraph>
                Here you can manage booking requests for items you've listed. Respond promptly to requests
                and keep your borrowers informed about any changes. If you have any questions, please
                contact our support team!
            </Typography>

            <List>
                {bookingCategories.map((category, index) => (
                    <React.Fragment key={category.title}>
                        <BookingCategoryItem
                            title={category.title}
                            count={category.count}
                            description={category.description}
                            icon={category.icon}
                            to={category.to}
                        />
                        {index < bookingCategories.length - 1 && (
                            <Divider variant="inset" component="li" />
                        )}
                    </React.Fragment>
                ))}
            </List>
        </Container>
    );
}

export default LendingBookings;
